<template>
	<div
		:class="{ 'opacity-40 pointer-events-none': disabled }"
		class="flex flex-col w-full justify-between rounded-xl p-4 shadow bg-gradient-to-t from-ihh-purple via-ihh-pink to-ihh-pink"
		:product-id="product.id"
	>
		<div class="flex flex-col w-full flex-grow">
			<div class="flex space-y-2 flex-grow">
				<div class="flex items-center justify-center mr-4 sm:mr-6 w-full max-w-[25%] sm:max-w-32 sm:max-h-32 aspect-square rounded-xl p-3 shadow bg-ihh-white">
					<img class="max-w-full max-h-full object-center object-contain aspect-square" :src="currentImgSrc" @error="onImgError" />
				</div>
				<div class="flex flex-col w-full space-y-2">
					<div class="">
						<div class="flex text-sm text-ihh-midnight-gray font-semibold float-right pl-2 pb-2">
							<button @click="showDetails = true" class="flex scale-75 sm:scale-100">
								<InfoIcon />
								<span class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">Details</span>
							</button>
						</div>
						<router-link :to="`/products${product.customUrl?.url}`">
							<div class="text-sm sm:text-base font-semibold text-ihh-midnight-gray break-words">{{ product.name }}</div>
						</router-link>
						<!-- Out of Stock Warning (beneath product name) -->
						<div
							v-if="productAndVariantsOutOfStock && editingKit"
							class="mt-1 mb-1 px-2 py-1 rounded bg-ihh-coral text-white text-xs text-center font-medium inline-block w-auto"
						>
							Out of stock
						</div>
					</div>
					<div class="text-xs md:text-sm text-ihh-slate-gray capitalize font-thin">
						{{ (variant || product).currentPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
						<span v-if="variant?.optionValues?.length"> / {{ variant.optionValues?.map((ov) => ov.label)?.join(', ') }}</span>
					</div>
					<div class="md2:flex w-full gap-4 justify-between flex-grow hidden">
						<div class="w-full space-y-0 space-x-0 flex-grow flex">
							<div class="w-full space-y-2 self-end">
								<div class="space-y-2 flex flex-wrap w-full" v-for="po in product.productOptions" :key="po.id">
									<div class="text-ihh-slate-gray font-semibold text-xs">
										<span class="text-ihh-midnight-gray mr-2 opacity-80">{{ po.displayName }}:</span>
										<span class="opacity-50 font-normal">(Required)</span>
									</div>

									<div class="flex w-full flex-wrap gap-2 sm:gap-2">
										<ProductOptionButtons
											class="min-w-20"
											v-for="o in po.optionValues"
											:key="o.id"
											:option="o"
											:variants="product.variants"
											:selectedOptions="selectedOptions"
											:small="true"
											:inventoryTracking="product.inventoryTracking == 'variant'"
											@click="selectOption(o)"
										/>
									</div>
								</div>
							</div>
							<div class="flex self-end flex-grow justify-end pl-2 flex-col space-y-2">
								<div v-if="!optional" class="flex justify-between text-ihh-midnight-gray space-x-2 md:space-x-4 text-sm md:text-base">
									<button
										@click="decreaseQuantity"
										class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-4 sm:w-5 h-4 sm:h-5 p-1 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center cursor-pointer"
									>
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path d="M19 13H5v-2h14v2z" />
										</svg>
									</button>
									<div class="m-auto font-semibold">{{ quantity }}</div>
									<button
										@click="increaseQuantity"
										class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-4 sm:w-5 h-4 sm:h-5 p-1 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center cursor-pointer"
									>
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
											<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
										</svg>
									</button>
								</div>
								<button v-if="isBuilder" @click="$emit('update:quantity', 0)" class="px-3 py-2 rounded bg-ihh-dark-salmon text-ihh-midnight-gray text-xs">
									Remove
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="flex w-full gap-4 justify-between flex-grow md2:hidden mt-4">
				<div class="w-full space-y-0 space-x-0 flex-grow flex">
					<div class="space-y-2 flex flex-wrap w-full" v-for="po in product.productOptions" :key="po.id">
						<div class="text-ihh-slate-gray font-semibold text-xs">
							<span class="text-ihh-midnight-gray mr-2 opacity-80">{{ po.displayName }}:</span>
							<span class="opacity-50 font-normal">(Required)</span>
						</div>

						<div class="flex w-full flex-wrap gap-2 sm:gap-2 md:gap-3">
							<ProductOptionButtons
								class="min-w-16 w-24"
								v-for="o in po.optionValues"
								:key="o.id"
								:option="o"
								:variants="product.variants"
								:selectedOptions="selectedOptions"
								:small="true"
								:inventoryTracking="product.inventoryTracking == 'variant'"
								@click="selectOption(o)"
							/>
						</div>
					</div>
					<div class="flex self-end flex-grow justify-end pl-2">
						<button v-if="optional" @click="$emit('update:quantity', 0)" class="px-3 py-2 rounded bg-ihh-dark-salmon text-white text-xs">Remove</button>
						<div v-else class="flex justify-between text-ihh-midnight-gray space-x-2 md:space-x-4 text-sm md:text-base">
							<button
								@click="decreaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-4 sm:w-5 h-4 sm:h-5 p-1 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path d="M19 13H5v-2h14v2z" />
								</svg>
							</button>
							<div class="m-auto font-semibold">{{ quantity }}</div>
							<button
								@click="increaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-4 sm:w-5 h-4 sm:h-5 p-1 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<Modal v-if="showDetails" @hide="showDetails = false" maxWidth="max-w-xl">
			<div class="prose">
				<h2 class="text-center">Product Details</h2>
				<div v-html="product.description" />
			</div>
		</Modal>
	</div>
</template>

<script>
import InfoIcon from '../layout/info-icon.vue'
import Modal from '../layout/modal.vue'
import ProductOptionButtons from '../partials/product-option-buttons.vue'
import Swal from 'sweetalert2'

export default {
	components: { ProductOptionButtons, Modal, InfoIcon },
	inject: ['store'],
	props: ['product', 'variant', 'quantity', 'optional', 'isBuilder'],
	data() {
		return {
			showDetails: false,
			selectedOptions: [],
			initialQuantity: this.quantity,
			currentImgSrc: this.thumbnail,
		}
	},
	mounted() {
		this.selectOptions()
		this.currentImgSrc = this.thumbnail
		// setTimeout(() => {
		//     this.$forceUpdate()
		// }, 1000)
	},
	watch: {
		selectedOptions: {
			handler() {
				let variant = this.product?.variants?.find((v) => v.optionValues?.every((o) => this.selectedOptions?.find((so) => so.id == o.id)))
				this.$emit('update:variant', variant)
			},
			deep: true,
		},
		thumbnail(newVal) {
			this.currentImgSrc = newVal
		},
	},

	methods: {
		selectOptions() {
			for (let po of this.product.productOptions || []) {
				for (let o of po.optionValues || []) {
					o.optionId = po.id
				}
				// console.log('this.variant', this.variant)
				if (this.variant) {
					let variantOptionValues = this.variant.optionValues
					if (this.product.inventoryTracking === 'variant') {
						variantOptionValues = variantOptionValues.filter((v) => v.inventoryLevel)
					}
					this.selectedOptions = po.optionValues.filter((o) => variantOptionValues.find((vo) => vo.id === o.id))
					// this.selectedOptions = [...this.selectedOptions, ...selectedOptions]
				} else {
					// console.log('po.optionValues[0]', po.optionValues[0])
					this.selectOption(po.optionValues?.find((o) => o.isDefault))
				}
			}
		},
		selectOption(option) {
			if (!option) return
			let options = this.selectedOptions.filter((o) => o.optionId !== option.optionId)
			this.selectedOptions = [...options, option]
		},
		increaseQuantity() {
			if (!this.requiredOptionsSelected.value && !this.editingKit) {
				Swal.fire({
					text: `Please select ${this.requiredOptionsSelected.options.join(' and ')} options`,
					icon: 'warning',
					confirmButtonText: 'OK',
					confirmButtonColor: '#e59789',
				})
				return
			}
			if (this.quantity < this.currentStock || this.editingKit) {
				this.$emit('update:quantity', this.quantity + 1)
			} else {
				Swal.fire({
					icon: 'warning',
					title: `Only ${this.currentStock} left in stock!`,
					text: `"${this.product?.name}" has low stock.`,
					confirmButtonColor: '#e59789',
				})
			}
		},
		decreaseQuantity() {
			if (!this.requiredOptionsSelected.value && !this.editingKit) {
				Swal.fire({
					text: `Please select ${this.requiredOptionsSelected.options.join(' and ')} options`,
					icon: 'warning',
					confirmButtonText: 'OK',
					confirmButtonColor: '#e59789',
				})
				return
			}
			if (this.quantity > 0 || this.editingKit) {
				this.$emit('update:quantity', this.quantity - 1)
			}
		},
		onImgError() {
			this.currentImgSrc = '/images/obDefaultProductImage-min.jpg'
		},
	},
	computed: {
		disabled() {
			return this.productAndVariantsOutOfStock && !this.editingKit
		},
		editingKit() {
			return this.$route.path.includes('/edit')
		},
		variants() {
			return this.product.variants
		},
		thumbnail() {
			return this.product?.images?.find((i) => i?.isThumbnail)?.url || this.product?.images?.[0]?.url || '/images/obDefaultProductImage-min.jpg'
		},
		outOfStock() {
			return this.store.editingKit ? false : this.product.outOfStock
		},
		variantsOutOfStock() {
			return this.product?.variants?.every((v) => v.inventoryLevel <= 0) && this.product.inventoryTracking === 'variant'
		},
		productAndVariantsOutOfStock() {
			return this.product?.outOfStock || !this.product?.isVisible || this.variantsOutOfStock
		},
		lowStock() {
			if (this.product?.inventoryTracking == 'none') return false

			if (this.product?.inventoryTracking == 'variant') {
				return this.variant?.inventoryLevel <= 5
			} else if (this.product?.inventoryTracking == 'product') {
				return this.product?.inventoryLevel <= 5
			}
		},
		currentStock() {
			if (this.product?.inventoryTracking == 'none') return 500
			if (this.product?.inventoryTracking == 'variant') {
				return this.variant?.inventoryLevel
			} else if (this.product?.inventoryTracking == 'product') {
				return this.product?.inventoryLevel
			}
		},
		stockAvailable() {
			if (this.currentStock > this.quantity) {
				return true
			} else {
				return false
			}
		},
		requiredOptionsSelected() {
			let value = true
			let options = []
			if (!this.product?.productOptions?.length) return { value, options }

			if (this.variant?.id) {
				return { value, options }
			}

			this.product.productOptions.forEach((option) => {
				options.push(option.displayName?.toLowerCase())
			})

			return { value: false, options }
		},
	},
}
</script>

<style scoped>
button,
div {
	-webkit-tap-highlight-color: transparent !important;
	-moz-tap-highlight-color: transparent !important; /* For Firefox, if needed */
	tap-highlight-color: transparent !important; /* Standard property */
}
</style>
